import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, Video, Eye, EyeOff, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVideoTestimonials = () => {
  const [videos, setVideos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: '',
    project_name: '',
    video_url: '',
    thumbnail_url: '',
    description: '',
    is_active: true,
    order: 0,
  });

  useEffect(() => {
    fetchVideos();
    fetchProjects();
  }, []);

  const fetchVideos = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/video-testimonials/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast.error('Failed to load video testimonials');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const openAddModal = () => {
    setEditingId(null);
    setFormData({
      customer_name: '',
      project_name: '',
      video_url: '',
      thumbnail_url: '',
      description: '',
      is_active: true,
      order: videos.length,
    });
    setShowModal(true);
  };

  const openEditModal = (video) => {
    setEditingId(video.id);
    setFormData({
      customer_name: video.customer_name || '',
      project_name: video.project_name || '',
      video_url: video.video_url || '',
      thumbnail_url: video.thumbnail_url || '',
      description: video.description || '',
      is_active: video.is_active !== false,
      order: video.order || 0,
    });
    setShowModal(true);
  };

  const getYouTubeEmbedUrl = (url) => {
    // Convert YouTube watch URL to embed URL
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    return url;
  };

  const getYouTubeThumbnail = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) {
      return `https://img.youtube.com/vi/${match[2]}/hqdefault.jpg`;
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('admin_token');

    // Auto-generate thumbnail from YouTube URL if not provided
    const submitData = { ...formData };
    if (!submitData.thumbnail_url && submitData.video_url) {
      submitData.thumbnail_url = getYouTubeThumbnail(submitData.video_url);
    }
    // Convert YouTube URL to embed format
    submitData.video_url = getYouTubeEmbedUrl(submitData.video_url);

    try {
      if (editingId) {
        await axios.put(`${API}/video-testimonials/${editingId}`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Video testimonial updated successfully');
      } else {
        await axios.post(`${API}/video-testimonials`, submitData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Video testimonial added successfully');
      }
      setShowModal(false);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast.error('Failed to save video testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/video-testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Video deleted');
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Failed to delete video');
    }
  };

  const toggleActive = async (video) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.put(`${API}/video-testimonials/${video.id}`, 
        { is_active: !video.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchVideos();
      toast.success(`Video ${!video.is_active ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error('Failed to update video');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin/dashboard" className="flex items-center gap-3">
              <span className="text-xl font-bold text-gray-900">Infinity Group</span>
            </Link>
            <div className="flex items-center gap-4 text-sm">
              <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">Dashboard</Link>
              <Link to="/admin/projects" className="text-gray-600 hover:text-gray-900">Projects</Link>
              <Link to="/admin/video-testimonials" className="text-blue-600 font-medium">Videos</Link>
              <Link to="/admin/testimonials" className="text-gray-600 hover:text-gray-900">Reviews</Link>
              <Link to="/admin/settings" className="text-gray-600 hover:text-gray-900">Settings</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Speaks</h1>
            <p className="text-gray-500 mt-1">Manage video testimonials from customers</p>
          </div>
          <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            Add Video
          </Button>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800">
            <strong>Tip:</strong> Paste YouTube video URLs directly - they will be automatically converted to embed format. 
            Thumbnails are auto-generated from YouTube videos.
          </p>
        </div>

        {videos.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm">
            <Video size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-4">No video testimonials yet</p>
            <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
              Add Your First Video
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div 
                key={video.id} 
                className={`bg-white rounded-xl overflow-hidden shadow-md ${!video.is_active ? 'opacity-60' : ''}`}
              >
                <div className="aspect-video bg-gray-100 relative">
                  {video.thumbnail_url ? (
                    <img 
                      src={video.thumbnail_url} 
                      alt={video.customer_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Video size={48} className="text-gray-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                      <div className="w-0 h-0 border-t-8 border-t-transparent border-l-12 border-l-red-600 border-b-8 border-b-transparent ml-1"></div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">{video.customer_name}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${video.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {video.is_active ? 'Active' : 'Hidden'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{video.project_name}</p>
                  {video.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t">
                    <button
                      onClick={() => toggleActive(video)}
                      className={`p-2 rounded-lg ${video.is_active ? 'text-gray-500 hover:bg-gray-100' : 'text-green-600 hover:bg-green-50'}`}
                      title={video.is_active ? 'Hide' : 'Show'}
                    >
                      {video.is_active ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                    <button
                      onClick={() => openEditModal(video)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? 'Edit Video' : 'Add Video Testimonial'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={formData.customer_name}
                  onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Rajesh Sharma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
                <select
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.name}>{project.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video URL *</label>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="text-xs text-gray-500 mt-1">Paste YouTube or Vimeo URL</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Custom Thumbnail URL (Optional)</label>
                <input
                  type="url"
                  value={formData.thumbnail_url}
                  onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Leave empty to auto-generate from YouTube"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Brief description of the video"
                ></textarea>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600"
                />
                <label htmlFor="is_active" className="text-sm text-gray-700">Show on website</label>
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {editingId ? 'Update' : 'Add'} Video
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideoTestimonials;
